import { treeNodeFilterFunction } from "@/utils/formUtils";
import { DataNode } from "@rc-component/tree-select/es/interface";
import { TreeSelect } from "antd";
import groupBy from "lodash/groupBy";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEnumeration } from "../../../../common/api/queries";
import { UUID } from "../../../../typings/global";
import { AgentEducationType } from "../../../agent/enums";
import { AgentEducationBase } from "../../../agent/types";
import { formatAgentEducationName } from "../../../agent/utils";
import { EnumerationType } from "../../enums";

const groupFilter = (value: string) => value.startsWith("group-");

const introductionFilter = (education: AgentEducationBase) =>
  education.type === AgentEducationType.INTRODUCTION_WEBINAR ||
  education.type === AgentEducationType.INTRODUCTION_LECTURE;

type Props = {
  value?: UUID[];
  onChange?: (value: UUID[]) => void;
  allowClear?: boolean;
};

export const AgentEducationTreeSelect = ({ value, onChange, allowClear = true }: Props) => {
  const { t } = useTranslation();

  const educationEnums = useEnumeration(EnumerationType.AGENT_EDUCATIONS);

  const educationsTree = useMemo(() => {
    const introductions = educationEnums.filter(introductionFilter);
    const educationGroups = groupBy(
      educationEnums
        .filter((education) => !introductionFilter(education))
        .sort((educationA, educationB) => educationA.code.localeCompare(educationB.code)),
      (e) => e.type,
    );

    return [
      ...introductions.map((education) => ({ value: education.id, title: formatAgentEducationName(education) })),
      ...Object.keys(educationGroups).map<DataNode>((type) => ({
        value: `group-${type}`,
        title: t("agent.education.enums.agentEducationType." + type),
        children: educationGroups[type]
          ?.map<DataNode>((education) => ({
            value: education.id,
            title: formatAgentEducationName(education),
          }))
          .sort((first, second) => first.title?.toString()?.localeCompare(second.title?.toString() ?? "") || 0),
      })),
    ];
  }, [educationEnums]);

  return (
    <TreeSelect
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeCheckable
      maxTagCount="responsive"
      popupMatchSelectWidth={false}
      allowClear={allowClear}
      showSearch={{ filterTreeNode: treeNodeFilterFunction }}
      treeData={educationsTree}
      onChange={(data) => {
        if (!data) {
          return;
        }

        const values = data.filter((value) => !groupFilter(value));

        const groupValues = data
          .filter(groupFilter)
          .map((value) => value.split("group-")[1])
          .filter((value): value is string => !!value);

        const result = [
          ...values,
          ...educationEnums.filter((item) => groupValues.includes(item.type)).map((item) => item.id),
        ];

        onChange?.(result);
      }}
      value={value}
    />
  );
};
